// src/utils/resolveSupportContact.js
//
// Resolves which per-org "personal connection outlet" a given member should see.
// Contacts live on organizations/{id}.supportContacts, each tagged with the
// member types it serves (empty = everyone / default). Every surface (athlete
// dashboard, spouse/family, teen, kids) resolves through this one function so
// the routing rule stays identical everywhere.

export function resolveSupportContact(orgData, memberType) {
    const list = orgData?.supportContacts;
    if (Array.isArray(list)) {
        if (list.length === 0) return null;
        const match = list.find(c => Array.isArray(c.memberTypes) && c.memberTypes.includes(memberType));
        if (match) return match;
        // Fall back to a contact tagged for everyone (no member types selected).
        return list.find(c => !c.memberTypes || c.memberTypes.length === 0) || null;
    }
    // Legacy single-contact shape (pre multi-contact) — treat as everyone's.
    if (orgData?.supportContact?.enabled) return orgData.supportContact;
    return null;
}

// A contact is only worth surfacing if there's an actual way to reach them.
export function hasReachableContact(contact) {
    return !!(contact && (contact.email || contact.phone || contact.bookingUrl));
}

// Resolve the org's EAP/EFAP program for a given member (see docs/plans/EAP-TIEIN-PLAN.md).
// Lives on organizations/{id}.eapProgram, configured in the super-admin editor.
// Family members (spouse/child member types) only see it when the program is
// flagged family-eligible; a program with no phone AND no portal is unreachable
// and therefore never surfaced.
export function resolveEapProgram(orgData, memberType) {
    const eap = orgData?.eapProgram;
    if (!eap?.enabled) return null;
    // Reachable by ANY channel the provider offers — a program with only a text
    // line or a chat URL is still a way through to a person.
    if (!(eap.phone || eap.url || eap.crisisPhone || eap.smsNumber || eap.chatUrl || eap.appUrl || eap.email)) return null;
    const isFamilyMember = memberType === 'spouse' || memberType === 'child';
    if (isFamilyMember && !eap.familyEligible) return null;
    return eap;
}

// Anything for the "Talk to Someone" outlet to show?
export function hasSupportOutlet(orgData, memberType) {
    return hasReachableContact(resolveSupportContact(orgData, memberType))
        || !!resolveEapProgram(orgData, memberType);
}
